import { EventEmitter, Injectable } from '@angular/core';

declare let MediaRecorder: any;

export enum OutputFormat {
  WEBM_BLOB_URL,
  WEBM_BLOB,
}

export enum ErrorCase {
  USER_CONSENT_FAILED,
  RECORDER_TIMEOUT,
  ALREADY_RECORDING,
}

export enum RecorderState {
  INITIALIZING,
  INITIALIZED,
  RECORDING,
  PAUSED,
  STOPPING,
  STOPPED,
}

@Injectable()
export class AudioRecorderService {
  private chunks: Array<any> = [];
  protected recorderEnded = new EventEmitter();
  public recorderError = new EventEmitter<ErrorCase>();
  // tslint:disable-next-line
  private _recorderState = RecorderState.INITIALIZING;

  constructor() {}

  private recorder: any;

  async startRecording(): Promise<boolean> {
    if (this._recorderState === RecorderState.RECORDING) {
      this.recorderError.emit(ErrorCase.ALREADY_RECORDING);
    }
    if (this._recorderState === RecorderState.PAUSED) {
      this.resume();
      return Promise.resolve(false);
    }
    this._recorderState = RecorderState.INITIALIZING;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true }).then((mediaStream) => {
        this.recorder = new MediaRecorder(mediaStream);
        this._recorderState = RecorderState.INITIALIZED;
        this.addListeners();
        this.recorder.start();
        this._recorderState = RecorderState.RECORDING;
      });
      return Promise.resolve(true);
    } catch (err) {
      return Promise.resolve(false);
    }
  }

  pause() {
    if (this._recorderState === RecorderState.RECORDING) {
      this.recorder.pause();
      this._recorderState = RecorderState.PAUSED;
    }
  }

  resume() {
    if (this._recorderState === RecorderState.PAUSED) {
      this._recorderState = RecorderState.RECORDING;
      this.recorder.resume();
    }
  }

  stopRecording(outputFormat: OutputFormat) {
    this._recorderState = RecorderState.STOPPING;
    return new Promise((resolve, reject) => {
      this.recorderEnded.subscribe(
        (blob) => {
          this._recorderState = RecorderState.STOPPED;
          this.recorder?.stop();
          if (outputFormat === OutputFormat.WEBM_BLOB) {
            resolve(blob);
          }
          if (outputFormat === OutputFormat.WEBM_BLOB_URL) {
            const audioURL = URL.createObjectURL(blob);
            resolve(audioURL);
          }
        },
        (_) => {
          this.recorderError.emit(ErrorCase.RECORDER_TIMEOUT);
          reject(ErrorCase.RECORDER_TIMEOUT);
        },
      );
      this.recorder.stop();
    }).catch(() => {
      this.recorderError.emit(ErrorCase.USER_CONSENT_FAILED);
    });
  }

  getRecorderState() {
    return this._recorderState;
  }

  private addListeners() {
    this.recorder.ondataavailable = this.appendToChunks;
    this.recorder.onstop = this.recordingStopped;
  }

  private appendToChunks = (event: any) => {
    this.chunks.push(event.data);
  };
  private recordingStopped = (event: any) => {
    const blob = new Blob(this.chunks, { type: 'audio/webm' });
    this.chunks = [];
    this.recorderEnded.emit(blob);
    this.clear();
  };

  private clear() {
    this.recorder = null;
    this.chunks = [];
  }
}
